// Video Library for the Extractor view, permanent docked panel on desktop,
// on-demand drawer on mobile. Both share the same LibraryPanel body.
import { useState, useMemo } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from '@/components/ui/sheet'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Search,
  Film,
  Clock,
  HardDrive,
  X,
  SortAsc,
  SortDesc,
  Upload,
  Library,
} from 'lucide-react'
import { cn, formatFileSize } from '@/lib/utils'
import { Video } from '@/lib/local-types'

type SortField = 'name' | 'uploadDate' | 'size'
type SortOrder = 'asc' | 'desc'

interface VideoLibraryProps {
  videos: Video[]
  isLoading: boolean
  currentVideoId: string | null
  storageInfo: {
    used: number
    quota: number
    percentUsed: number
  }
  onSelectVideo: (video: Video) => void
  onUploadVideo: () => void
}

// Shared panel body (no positioning/width, the container decides that)
function LibraryPanel({
  videos,
  isLoading,
  currentVideoId,
  storageInfo,
  onSelectVideo,
  onUploadVideo,
}: VideoLibraryProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [sortField, setSortField] = useState<SortField>('uploadDate')
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc')

  // Filter and sort videos
  const filteredVideos = useMemo(() => {
    let result = [...videos]

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      result = result.filter(video =>
        video.name.toLowerCase().includes(query)
      )
    }

    // Sort videos
    result.sort((a, b) => {
      let comparison = 0
      switch (sortField) {
        case 'name':
          comparison = a.name.localeCompare(b.name)
          break
        case 'uploadDate':
          comparison = new Date(a.uploadDate).getTime() - new Date(b.uploadDate).getTime()
          break
        case 'size':
          comparison = a.size - b.size
          break
      }
      return sortOrder === 'asc' ? comparison : -comparison
    })

    return result
  }, [videos, searchQuery, sortField, sortOrder])

  const toggleSortOrder = () => {
    setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Header */}
      <div className="flex items-center gap-2 p-4 border-b">
        <Film className="h-5 w-5" />
        <h2 className="font-semibold">Video Library</h2>
        <Badge variant="secondary" className="ml-1">
          {videos.length}
        </Badge>
      </div>

      {/* Storage info */}
      <div className="px-4 py-2 border-b bg-muted/30">
        <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
          <span className="flex items-center gap-1">
            <HardDrive className="h-3 w-3" />
            Storage
          </span>
          <span>{formatFileSize(storageInfo.used)} / {formatFileSize(storageInfo.quota)}</span>
        </div>
        <div className="h-1.5 bg-muted rounded-full overflow-hidden">
          <div
            className={cn(
              'h-full rounded-full transition-all',
              storageInfo.percentUsed > 90 ? 'bg-destructive' :
              storageInfo.percentUsed > 70 ? 'bg-yellow-500' : 'bg-primary'
            )}
            style={{ width: `${Math.min(100, storageInfo.percentUsed)}%` }}
          />
        </div>
      </div>

      {/* Search and filters */}
      <div className="p-3 border-b space-y-2">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search videos..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 pr-8 h-9"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Sort controls */}
        <div className="flex items-center gap-2">
          <Select value={sortField} onValueChange={(v) => setSortField(v as SortField)}>
            <SelectTrigger className="h-8 flex-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="uploadDate">Date Added</SelectItem>
              <SelectItem value="name">Name</SelectItem>
              <SelectItem value="size">File Size</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8 flex-shrink-0"
            onClick={toggleSortOrder}
          >
            {sortOrder === 'asc' ? (
              <SortAsc className="h-4 w-4" />
            ) : (
              <SortDesc className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>

      {/* Video list, force the Radix viewport content to block so cards
          can't shrink-wrap wider than the panel (else the badge clips) */}
      <ScrollArea className="flex-1 [&_[data-slot=scroll-area-viewport]>div]:!block">
        <div className="p-2 space-y-1">
          {isLoading ? (
            // Loading skeletons
            Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="p-2 space-y-2">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            ))
          ) : filteredVideos.length === 0 ? (
            // Empty state
            <div className="py-8 text-center">
              {searchQuery ? (
                <>
                  <Search className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">No videos match your search</p>
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => setSearchQuery('')}
                  >
                    Clear search
                  </Button>
                </>
              ) : (
                <>
                  <Film className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground mb-3">No videos uploaded yet</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onUploadVideo}
                    className="gap-2"
                  >
                    <Upload className="h-4 w-4" />
                    Upload Video
                  </Button>
                </>
              )}
            </div>
          ) : (
            // Video items
            filteredVideos.map((video) => (
              <VideoItem
                key={video.id}
                video={video}
                isSelected={video.id === currentVideoId}
                onClick={() => onSelectVideo(video)}
              />
            ))
          )}
        </div>
      </ScrollArea>

      {/* Upload button footer */}
      <div className="p-3 border-t space-y-2">
        <Button
          variant="outline"
          className="w-full gap-2"
          onClick={onUploadVideo}
        >
          <Upload className="h-4 w-4" />
          Upload New Video
        </Button>
        <p className="text-xs text-center text-muted-foreground">
          Supported: MP4, WebM, Ogg
        </p>
      </div>
    </div>
  )
}

// Desktop: permanent docked panel
export function VideoLibrarySidebar(props: VideoLibraryProps) {
  return (
    <aside className="hidden md:flex flex-col w-80 shrink-0 h-full min-h-0 bg-background border-r">
      <LibraryPanel {...props} />
    </aside>
  )
}

// Mobile: on-demand drawer with its own trigger button
export function VideoLibraryDrawer(props: VideoLibraryProps) {
  const [open, setOpen] = useState(false)

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Library className="h-4 w-4" />
          Library
          <Badge variant="secondary" className="ml-0.5">{props.videos.length}</Badge>
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="w-[85vw] max-w-sm p-0">
        <LibraryPanel
          {...props}
          onSelectVideo={(video) => {
            props.onSelectVideo(video)
            setOpen(false)
          }}
          onUploadVideo={() => {
            setOpen(false)
            props.onUploadVideo()
          }}
        />
      </SheetContent>
    </Sheet>
  )
}

// Individual video item component
interface VideoItemProps {
  video: Video
  isSelected: boolean
  onClick: () => void
}

function VideoItem({ video, isSelected, onClick }: VideoItemProps) {
  const uploadDate = new Date(video.uploadDate)
  const formattedDate = uploadDate.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: uploadDate.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
  })

  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full text-left p-3 rounded-lg transition-colors',
        'hover:bg-muted/50',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        isSelected && 'bg-primary/10 border border-primary/30 hover:bg-primary/15'
      )}
    >
      <div className="flex items-start gap-3">
        {/* Thumbnail placeholder */}
        <div className={cn(
          'w-16 h-10 rounded bg-muted flex-shrink-0 flex items-center justify-center',
          isSelected && 'ring-2 ring-primary'
        )}>
          <Film className="h-4 w-4 text-muted-foreground" />
        </div>

        <div className="flex-1 min-w-0">
          {/* Video name */}
          <p className={cn(
            'font-medium text-sm truncate',
            isSelected && 'text-primary'
          )}>
            {video.name}
          </p>

          {/* Metadata */}
          <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {video.duration || '--:--'}
            </span>
            <span>•</span>
            <span>{formatFileSize(video.size)}</span>
          </div>

          {/* Upload date */}
          <p className="text-xs text-muted-foreground mt-0.5">
            {formattedDate}
          </p>
        </div>

        {/* Selected indicator */}
        {isSelected && (
          <Badge variant="default" className="text-xs flex-shrink-0">
            Active
          </Badge>
        )}
      </div>
    </button>
  )
}
